import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { locationService } from '../../services/locationService';
import './ShippingForm.css';

const emptyDefaults = {
  fullName: '',
  address: '',
  city: '',
  phone: '',
  provinceId: '',
  districtId: '',
};

const ShippingForm = ({ onSubmit, defaultValues, savedAddresses = [], onAddressSelect }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    defaultValues: { ...emptyDefaults, ...(defaultValues || {}) },
  });

  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [saveToAddressBook, setSaveToAddressBook] = useState(true);

  const selectedProvinceId = watch('provinceId');

  // Load provinces once
  useEffect(() => {
    locationService.getProvinces().then(setProvinces).catch(() => setProvinces([]));
  }, []);

  // Load districts when province changes
  useEffect(() => {
    if (!selectedProvinceId) {
      setDistricts([]);
      return;
    }
    locationService
      .getDistricts(selectedProvinceId)
      .then(setDistricts)
      .catch(() => setDistricts([]));
  }, [selectedProvinceId]);

  // Reset when external defaults change (e.g. user picked a saved address)
  useEffect(() => {
    if (defaultValues) {
      reset({ ...emptyDefaults, ...defaultValues });
    }
  }, [defaultValues, reset]);

  const selectAddress = (id) => {
    setSelectedAddressId(id);
    if (id === 'new') {
      reset(emptyDefaults);
      setSaveToAddressBook(true);
      onAddressSelect?.(null);
      return;
    }
    const addr = savedAddresses.find((a) => a.id === id);
    if (!addr) return;
    reset({
      fullName: addr.full_name || '',
      address: addr.address || '',
      city: addr.city || '',
      phone: addr.phone || '',
      provinceId: addr.province_id || '',
      districtId: addr.district_id || '',
    });
    setSaveToAddressBook(false);
    onAddressSelect?.(addr);
  };

  // Auto-select default saved address on first load
  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (!savedAddresses.length) return;
    const preferred =
      savedAddresses.find((a) => a.is_default) || savedAddresses[0];
    if (!preferred) return;
    didAutoSelectRef.current = true;
    selectAddress(preferred.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedAddresses]);

  // Hide district if user changes province manually after picking a saved address
  useEffect(() => {
    if (
      selectedAddressId !== 'new' &&
      selectedProvinceId &&
      districts.length > 0
    ) {
      const current = watch('districtId');
      if (current && !districts.find((d) => d.id === current)) {
        setValue('districtId', '');
      }
    }
  }, [districts, selectedAddressId, selectedProvinceId, setValue, watch]);

  const submit = (data) => {
    const province = provinces.find((p) => p.id === data.provinceId);
    const district = districts.find((d) => d.id === data.districtId);
    onSubmit({
      ...data,
      provinceName: province?.name || '',
      districtName: district?.name || '',
      saveToAddressBook,
      selectedAddressId: selectedAddressId === 'new' ? null : selectedAddressId,
    });
  };

  const isNewAddress = selectedAddressId === 'new';

  return (
    <form onSubmit={handleSubmit(submit)} className="shipping-form">
      <h3>Shipping Information</h3>

      {savedAddresses.length > 0 && (
        <div className="address-picker">
          <div className="address-picker-header">
            <span className="address-picker-title">Deliver to</span>
            {!isNewAddress && (
              <button
                type="button"
                className="address-picker-action"
                onClick={() => selectAddress('new')}
              >
                + Add new address
              </button>
            )}
          </div>

          <div className="address-cards">
            {savedAddresses.map((addr) => {
              const selected = selectedAddressId === addr.id;
              return (
                <button
                  type="button"
                  key={addr.id}
                  className={`address-card ${selected ? 'is-selected' : ''}`}
                  onClick={() => selectAddress(addr.id)}
                  aria-pressed={selected}
                >
                  <div className="address-card-top">
                    <span className="address-card-name">
                      {addr.label || addr.full_name}
                    </span>
                    {addr.is_default && (
                      <span className="address-card-badge">Default</span>
                    )}
                  </div>
                  {addr.label && (
                    <div className="address-card-line">{addr.full_name}</div>
                  )}
                  <div className="address-card-line">{addr.address}</div>
                  <div className="address-card-line">
                    {[addr.city, addr.district?.name, addr.province?.name]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                  {addr.phone && (
                    <div className="address-card-line address-card-phone">
                      {addr.phone}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!isNewAddress && savedAddresses.length > 0 ? null : (
      <>
      {savedAddresses.length > 0 && (
        <div className="address-form-divider">
          <span>New address</span>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="fullName">Full Name *</label>
        <input
          id="fullName"
          type="text"
          {...register('fullName', { required: 'Full name is required' })}
        />
        {errors.fullName && <span className="error">{errors.fullName.message}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="address">Street Address / Locality *</label>
        <textarea
          id="address"
          rows="3"
          {...register('address', { required: 'Address is required' })}
        />
        {errors.address && <span className="error">{errors.address.message}</span>}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="provinceId">Province *</label>
          <select
            id="provinceId"
            {...register('provinceId', { required: 'Province is required' })}
          >
            <option value="">Select province</option>
            {provinces.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          {errors.provinceId && <span className="error">{errors.provinceId.message}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="districtId">District *</label>
          <select
            id="districtId"
            disabled={!selectedProvinceId || districts.length === 0}
            {...register('districtId', { required: 'District is required' })}
          >
            <option value="">
              {selectedProvinceId ? 'Select district' : 'Pick a province first'}
            </option>
            {districts.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          {errors.districtId && <span className="error">{errors.districtId.message}</span>}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="city">City / Town</label>
        <input id="city" type="text" {...register('city')} />
      </div>

      <div className="form-group">
        <label htmlFor="phone">Phone Number *</label>
        <input
          id="phone"
          type="tel"
          {...register('phone', {
            required: 'Phone number is required',
            pattern: { value: /^[0-9]{10}$/, message: 'Phone number must be 10 digits' },
          })}
        />
        {errors.phone && <span className="error">{errors.phone.message}</span>}
      </div>

      <div className="form-group form-checkbox">
        <label>
          <input
            type="checkbox"
            checked={saveToAddressBook}
            onChange={(e) => setSaveToAddressBook(e.target.checked)}
          />
          Save this address to my address book for next time
        </label>
      </div>
      </>
      )}

      <button type="submit" className="btn-primary">
        {isNewAddress || savedAddresses.length === 0
          ? 'Continue'
          : 'Continue with this address'}
      </button>
    </form>
  );
};

export default ShippingForm;
